<script lang="ts">
  import Drawer from '$components/drawer.svelte';
  import { CART_DRAWER_ID, FREE_GIFT, GUEST } from '$lib/constants';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { formatCurrency } from '$lib/utils/currency';
  import { t } from '$lib/i18n';
  import OrderSummary from './order-summary.svelte';
  import CartItem from './cart-item.svelte';
  import DeliveredOn from '$components/delivered-on.svelte';
  import Button from '$components/ui/button.svelte';
  import { cn } from '$lib/utils';
  import {
    AddToCartLocations,
    AnalyticsEvents,
    ElevarAnalyticsEvents,
    analyticsEvent,
    elevarAnalyticsEvent,
    parseCartToAnalyticsEvent,
    type ElevarImpression
  } from '$lib/analytics';
  import { drawer } from '$lib/stores/drawer';
  import { page } from '$app/stores';
  import { getAnchorFromCmsLink, getImageAttributes, isCmsStory } from '$lib/utils/cms';
  import { defaultCurrency, region } from '$lib/region';
  import { geolocation } from '$lib/stores/geolocation';
  import { cart, checkoutUrl } from '$lib/cart';
  import Icon from '$components/ui/icon/icon.svelte';
  import ProgressBar from '$components/progress-bar.svelte';
  import {
    cartFilteredByTierForFreeGift,
    freeGiftCampaign,
    getCampaignColors,
    progressBarConfig
  } from '$lib/stores/campaigns';
  import QuickAddCardLoader from '$components/quick-add-card-loader.svelte';
  import QuickAddCard from '$components/quick-add-card.svelte';
  import CartRecommendations from './cart-recommendations.svelte';
  import CartDiscount from './cart-discount.svelte';
  import { trimVariantId } from '$lib/utils/shopify/productsId';
  import ChangeGift, { selectedGift } from './change-gift.svelte';
  import ChangeSize from './change-size.svelte';
  import type { GetCartQuery } from '$gql/storefront/operations';
  import { toaster } from '$lib/toaster';
  import { updateGift } from '$lib/cart/actions';
  import { cartProducts } from '$lib/cart/cart';
  import type { CartGiftBoxProductStoryblok } from '$types/bloks';
  import { slide } from 'svelte/transition';

  $: items = $cart?.lines.nodes.filter((line) => line.quantity > 0) ?? [];
  $: outOfStockItems = $cart?.lines.nodes.filter((line) => line.quantity === 0) ?? [];

  $: cartGiftBoxesByRegion = $page.data.configuration.content.cart_gift_boxes?.find(
    (item: CartGiftBoxProductStoryblok) => item.region === $region
  );
  $: giftBagHandles = (cartGiftBoxesByRegion?.products || []).flatMap((giftBagProduct) =>
    isCmsStory(giftBagProduct) ? [giftBagProduct.slug] : []
  );

  // Analytics
  drawer.subscribe((val) => {
    if (val.id === CART_DRAWER_ID) {
      if ($cart) {
        analyticsEvent({
          event: AnalyticsEvents.VIEW_CART,
          ...parseCartToAnalyticsEvent($cart)
        });
      }

      elevarAnalyticsEvent({
        event: ElevarAnalyticsEvents.VIEW_CART,
        cart_total: `${+$cart?.cost?.totalAmount?.amount || 0.0}`,
        user_properties: {
          visitor_type: GUEST
        },
        ecommerce: {
          currencyCode: $cart?.cost?.totalAmount?.currencyCode || defaultCurrency,
          actionField: {
            list: 'Shopping Cart'
          },
          impressions: $cartProducts('elevarImpressions') as ElevarImpression[]
        }
      });
    }
  });

  // Only show tax on US store if user is buying from US or CAN
  $: shouldShowTax =
    $region === 'us' && ($geolocation?.country === 'US' || $geolocation?.country === 'CA');

  // Shopify is calculating a wrong total and sometimes it adds $100 USD shipping
  // Unfortunately, we need to sum manually the tax + subtotal
  $: total = shouldShowTax
    ? Number($cart?.cost.subtotalAmount.amount) + Number($cart?.cost.totalTaxAmount?.amount ?? 0)
    : $cart?.cost.subtotalAmount.amount;

  const cartState = cart.state;
  const checkoutUrlState = checkoutUrl.state;

  $: shopPayUrl = (() => {
    if ($cart?.checkoutUrl) {
      const url = new URL($cart?.checkoutUrl);
      url.searchParams.append('payment', 'shop_pay');
      return url.toString();
    }
  })();

  $: cartDescription = $page.data.configuration.content.cart_promotional_blocks?.find((item) =>
    item.regions.includes($region)
  )?.label;

  let altTextColor: string | undefined = undefined;

  $: campaignColors = $progressBarConfig
    ? getCampaignColors($progressBarConfig?.campaign_color)
    : null;
  $: isBlackCampaignColor = campaignColors?.isBlackColor;
  $: altTextColor = campaignColors?.altTextColor;

  $: showFreeGiftCards = !!$drawer.meta?.showFreeGiftCards;

  let selectedProduct: NonNullable<GetCartQuery['cart']>['lines']['nodes'][number] | undefined =
    undefined;
  let loading = false;

  const selectProduct = (e: CustomEvent) => {
    e.preventDefault();
    selectedProduct = e.detail;
    selectedGift.set({
      handle: selectedProduct?.merchandise?.product?.handle as string,
      variantId: trimVariantId(selectedProduct?.merchandise?.id as string)
    });
  };

  const resetGift = () => {
    selectedGift.update((curr) => {
      return {
        ...curr,
        handle: '',
        variantId: undefined,
        sellingPlanId: undefined
      };
    });
  };

  const handleGiftChange = () => {
    // update the gifts only if a new item is selected
    loading = true;
    $updateGift()
      .then(() => {
        drawer.open(CART_DRAWER_ID, {
          meta: undefined
        });
      })
      .finally(() => {
        toaster({
          type: 'success',
          title: `${$t('cart.gift-size')}`
        });
        loading = false;
        selectedProduct = undefined;
      });
  };

  $: freeGiftExists = items.find((i) => i.attributes?.some((att) => att.key == FREE_GIFT));

  $: cartFilteredByTierForFreeGiftQuantity =
    $cartFilteredByTierForFreeGift?.lines.nodes.reduce((acc, line) => {
      return acc + line.quantity;
    }, 0) || 0;

  $: showFreeGiftBanner =
    $cart &&
    $freeGiftCampaign &&
    !$freeGiftCampaign.add_gift_automatically &&
    cartFilteredByTierForFreeGiftQuantity >=
      Number($freeGiftCampaign.show_free_gift_on_tier_level) &&
    !$cart.lines.nodes.some((line) => line.attributes?.some((attr) => attr.key === FREE_GIFT));

  $: if (
    freeGiftExists &&
    ($freeGiftCampaign?.free_gift_cart_position == 'first' ||
      $freeGiftCampaign?.free_gift_cart_position == 'last')
  ) {
    items.sort((a, b) => {
      const isAFreeGift = a.attributes?.some((att) => att.key == FREE_GIFT);
      const isBFreeGift = b.attributes?.some((att) => att.key == FREE_GIFT);

      if (isAFreeGift && !isBFreeGift) {
        return $freeGiftCampaign?.free_gift_cart_position == 'first' ? -1 : 1;
      } else if (!isAFreeGift && isBFreeGift) {
        return $freeGiftCampaign?.free_gift_cart_position == 'first' ? 1 : -1;
      } else {
        return 0;
      }
    });
  }
</script>

<Drawer id={CART_DRAWER_ID}>
  <div
    slot="header"
    class="absolute top-0 z-50 flex h-12 w-full items-center justify-between border-b border-primary/2 px-4"
  >
    {#if showFreeGiftCards}
      <button
        aria-label="close"
        class="flex h-fit"
        on:click={() => {
          resetGift();
          drawer.open(CART_DRAWER_ID, {
            meta: undefined
          });
        }}
      >
        <Icon size="lg" icon="arrow-left" />
      </button>
      <p class="text-md font-medium absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {$t('cart.change-gift')}
      </p>
    {:else}
      <p class="text-md font-semibold" id="dialog-title">{$t('cart.drawer-title')}</p>
      <button aria-label="close" class="flex h-fit" on:click={() => drawer.close()}>
        <Icon size="lg" icon="cross" />
      </button>
    {/if}
  </div>

  {#if showFreeGiftCards}
    <ChangeGift />
  {:else}
    <div class="flex flex-col h-full" style:--altTextColor={altTextColor}>
      {#if $progressBarConfig || cartDescription}
        <div class="m-2 p-1.5 bg-highlight/88 rounded-md">
          {#if $progressBarConfig}
            <ProgressBar showConfettiAnimation={false} />
          {/if}
          {#if cartDescription}
            <p
              class={cn(
                'text-[10px] sm:text-xs mb-0.5 mx-1 text-[var(--altTextColor)]',
                isBlackCampaignColor && 'text-accent',
                $progressBarConfig ? 'mt-2' : 'mt-0.5'
              )}
            >
              {cartDescription}
            </p>
          {/if}
        </div>
      {/if}

      {#if $cart && $cartState?.isLoading}
        <section class="p-4">
          <Skeleton class="mb-8 h-10 w-80 rounded-xs" />
          {#each new Array(3) as _}
            <Skeleton class="mb-2 h-20 w-full rounded-xs" />
          {/each}
        </section>
      {:else if $cartState?.isError}
        <div class="flex flex-1 w-full flex-col items-center justify-center text-accent">
          <p class="text-xl font-semibold">{$t('error.unexpected.title')}</p>
          <p class="mt-1 opacity-40">{$t('error.unexpected.description')}</p>
        </div>
      {:else if !$cart || ($cart && $cart.totalQuantity === 0)}
        <div class="flex flex-1 w-full flex-col items-center justify-center">
          <p class="text-xl font-semibold">{$t('cart.empty.title')}</p>
          <p class="mt-1 opacity-40">{$t('cart.empty.description')}</p>
          {#if $page.data.configuration.content.cart_empty_cta?.[0].url}
            {@const { href, rel, target } = $getAnchorFromCmsLink(
              $page.data.configuration.content.cart_empty_cta[0].url
            )}
            {#if href !== $page.url.pathname}
              <Button
                name={$page.data.configuration.content.cart_empty_cta[0].text}
                class="mt-4"
                variant="accent"
                as="a"
                {href}
                {rel}
                {target}>{$page.data.configuration.content.cart_empty_cta[0].text}</Button
              >
            {:else}
              <Button
                name={$page.data.configuration.content.cart_empty_cta[0].text}
                class="mt-4"
                variant="accent"
                on:click={() => drawer.close()}
                >{$page.data.configuration.content.cart_empty_cta[0].text}</Button
              >
            {/if}
          {/if}
        </div>
      {:else if $cart?.id}
        <section class="relative px-2 flex flex-col h-full">
          <div class="flex-1">
            {#if showFreeGiftBanner && $freeGiftCampaign}
              <button
                on:click={() =>
                  drawer.open(CART_DRAWER_ID, {
                    meta: {
                      showFreeGiftCards: true
                    }
                  })}
                class="w-full relative h-[100px] rounded-md overflow-hidden"
                transition:slide={{ duration: 200 }}
              >
                <div class="absolute inset-0 h-full w-full">
                  <img
                    loading="lazy"
                    src={getImageAttributes($freeGiftCampaign.cart_banner_image, { size: [500, 0] })
                      .src}
                    alt="newsletter"
                    class="object-cover xl:object-contain h-full xl:h-auto w-full xl:w-auto"
                  />
                </div>

                <div class="absolute inset-0 h-full w-full bg-primary/40"></div>
                <div class=" absolute inset-0 h-full w-full flex justify-between flex-col p-2">
                  <p class="text-md font-semibold text-highlight w-32 text-left">
                    {$freeGiftCampaign.cart_banner_title_label}
                  </p>
                  <div
                    class="bg-primary/18 text-highlight border-0 hover:bg-primary/14 w-fit gap-1 px-2 h-8 text-sm/none rounded-xs inline-flex items-center justify-center"
                  >
                    <Icon size="xs" icon="gift" />
                    <p>
                      {$freeGiftCampaign.cart_banner_select_gift_button_label}
                    </p>
                  </div>
                </div></button
              >
            {/if}
            <h2 class="mb-4 mt-8 px-2 text-2xl font-semibold">
              {$t('cart.your-total', {
                value: $formatCurrency($cart.cost.subtotalAmount.currencyCode, total)
              })}
            </h2>
            <div class="flex flex-col gap-2">
              {#each items as line (line.id)}
                <CartItem data={line} on:select={selectProduct} />
              {/each}
            </div>
            {#if outOfStockItems.length > 0}
              <div class="my-6">
                <h3 class="px-2 text-xl font-semibold">{$t('cart.out-of-stock.title')}</h3>
                <div class="mt-4 flex flex-col gap-2">
                  {#each outOfStockItems as line (line.id)}
                    <CartItem data={line} />
                  {/each}
                </div>
              </div>
            {/if}

            <CartDiscount />

            <OrderSummary {shouldShowTax} {total} />

            {#if giftBagHandles.length > 0}
              <div class="my-12">
                <h3 class="px-2 text-xl font-semibold mb-4">
                  {$t('cart.add-gift-box')}
                </h3>
                <div class="flex flex-col gap-2">
                  {#each giftBagHandles as giftBagHandle}
                    {#if !$cart?.lines.nodes.some((line) => line.merchandise.product.handle === giftBagHandle)}
                      <QuickAddCardLoader
                        handle={giftBagHandle}
                        enabled={false}
                        let:loading
                        let:product
                        let:load
                      >
                        <QuickAddCard
                          on:intersect={load}
                          {loading}
                          {product}
                          location={AddToCartLocations.CART_GIFT_BOX}
                        />
                      </QuickAddCardLoader>
                    {/if}
                  {/each}
                </div>
              </div>
            {/if}

            {#if items.length > 0}
              {@const priciestItem = items.reduce((acc, curr) => {
                return acc.cost.totalAmount.amount > curr.cost.totalAmount.amount ? acc : curr;
              }, items[0])}
              <CartRecommendations
                cartItemsIds={items.map((item) => item.merchandise.product.id)}
                productId={priciestItem.merchandise.product.id}
              />
            {/if}
          </div>

          <div
            class={cn(
              'sticky bottom-0 -mx-2 mt-2 p-4 transition-all duration-300 border-t bg-background'
            )}
          >
            {#if selectedProduct && $selectedGift?.variantId}
              <ChangeSize
                product={selectedProduct.merchandise.product}
                {loading}
                on:close={() => (selectedProduct = undefined)}
                on:save={() => handleGiftChange()}
              />
            {:else}
              <DeliveredOn />
              {#if $page.data.configuration.content?.payment_methods}
                <div class="mt-3 flex flex-wrap items-center gap-2 border-y py-2">
                  {#each $page.data.configuration.content?.payment_methods as payment_method}
                    {#if payment_method.filename}
                      <img
                        src={payment_method.filename}
                        alt={payment_method.alt}
                        class="w-[35px] lg:w-[29px]"
                        width="35"
                        height="24"
                      />
                    {/if}
                  {/each}
                </div>
              {/if}
              <div class="mt-3 flex gap-2">
                {#if $region !== 'eu'}
                  <Button
                    name={$t('cart.shop-pay')}
                    data-sveltekit-reload
                    as="a"
                    href={shopPayUrl}
                    variant="primary"
                    class="w-full bg-white hover:bg-white/80 active:bg-white/60"
                    on:click={() => {
                      if ($cart) {
                        analyticsEvent({
                          event: AnalyticsEvents.BEGIN_CHECKOUT,
                          ...parseCartToAnalyticsEvent($cart)
                        });
                      }
                    }}
                  >
                    <Icon icon="shop" class="w-auto h-auto" />
                  </Button>
                {/if}
                <Button
                  name="checkout"
                  data-sveltekit-reload
                  loading={$checkoutUrlState?.isLoading}
                  disabled={!$checkoutUrl}
                  as="a"
                  href={$checkoutUrl}
                  class="w-full"
                  on:click={() => {
                    if ($cart) {
                      analyticsEvent({
                        event: AnalyticsEvents.BEGIN_CHECKOUT,
                        ...parseCartToAnalyticsEvent($cart)
                      });
                    }
                  }}
                >
                  {$t('cart.checkout')}
                </Button>
              </div>
            {/if}
          </div>
        </section>
      {/if}
    </div>
  {/if}
</Drawer>
